import * as v from 'valibot';

import { ENOPOrganType } from '../../shared';
import {
  ENOPCaseStatus,
  ENOPDonorType,
  ENOPRoutePlanningStatus,
} from '../enums';

import { hospitalSchema } from './hospital.schema';
import { organSchema } from './organ.schema';
import { trialProgramSchema } from './trialProgram.schema';
import { fallbackWithIssue } from './valibot.helpers';

import type { NewCase } from '../types';

export const newCaseSchema: v.GenericSchema<NewCase> = v.object({
  id: fallbackWithIssue(
    v.string('Case ID is required'),
    '---',
    '@WARNING: Case ID is missing',
  ),
  unosDonorId: fallbackWithIssue(
    v.string('UNOS Donor ID is required'),
    '---',
    '@WARNING: UNOS Donor ID is missing',
  ),
  label: fallbackWithIssue(
    v.string('Case Label is required'),
    '---',
    '@WARNING: Case Label is missing or corrupted',
  ),
  donorType: fallbackWithIssue(
    v.enum(
      ENOPDonorType,
      issue => `@WARNING: Unexpected donor type ${issue.input}`,
    ),
    ENOPDonorType.DBD,
    '@WARNING: Donor type is missing or corrupted',
  ),
  donorORDateTimeUtc: v.pipe(
    v.string('@WARNING: Donor OR DateTime is missing'),
    v.isoTimestamp('@WARNING: Donor OR DateTime is incorrect'),
  ),
  donorORTimeString: fallbackWithIssue(
    v.string(),
    '--:--',
    '@WARNING: Donor OR time string is missing or invalid',
  ),
  oscStatus: fallbackWithIssue(
    v.enum(
      ENOPCaseStatus,
      issue => `@WARNING: Unexpected case status ${issue.input}`,
    ),
    ENOPCaseStatus.Accepted,
    '@WARNING: Case status is missing or corrupted',
  ),

  caseTimerEndString: fallbackWithIssue(
    v.nullable(v.string()),
    null,
    '@WARNING: Case timer end string is missing or invalid',
  ),
  organ: fallbackWithIssue(
    organSchema,
    {
      id: ENOPOrganType.Heart,
      name: ENOPOrganType.Heart,
      startPoint: 0,
    },
    '@WARNING: Organ is missing or corrupted',
  ),
  donorHospital: v.nonNullish(
    hospitalSchema,
    '@WARNING: Donor Address is missing or corrupted',
  ),
  // Null until NOP knows the transplant centre. Not `nonNullish`: that leaves
  // `safeParse` untyped on null, and applyEntityFrame drops an untyped frame.
  recipientHospital: v.nullable(hospitalSchema),
  transplantCenterCode: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: Transplant center code is missing or corrupted',
  ),
  showOnMap: fallbackWithIssue(
    v.boolean('Show On Map is required'),
    false,
    '@WARNING: Show On Map is missing or corrupted',
  ),
  trialProgram: v.nullable(trialProgramSchema),
  serviceRegionId: v.optional(v.string()),
  routePlanningStatus: v.nullish(v.enum(ENOPRoutePlanningStatus)),
  routePlanningStatusSince: v.nullish(
    v.pipe(
      v.string('@WARNING: Route planning status since is not a string'),
      v.isoTimestamp(
        '@WARNING: Route planning status since is not an ISO time',
      ),
    ),
  ),
  primaryLogisticsManager: v.optional(
    v.nullable(
      v.object({
        firstName: v.nullable(v.string()),
        lastName: v.nullable(v.string()),
        role: v.nullable(v.string()),
        email: v.nullable(v.string()),
        phone: v.nullable(v.string()),
      }),
    ),
  ),
});
